/**
 * FloatingAddSegmentButton.tsx - Floating action button for adding segments to switch nodes
 *
 * Appears when a switch/conditional node is selected, positioned near the node.
 * Clicking adds a new case/segment to the selected switch step.
 */

import { css } from 'aphrodite';
import { useCallback, useState } from 'react';
import { Panel } from '@xyflow/react';
import type * as Api from '@ViewModels';
import { styleSheet } from './styles/FloatingAddSegmentButtonStyles';

interface FloatingAddSegmentButtonProps {
	/** ID of the selected switch node */
	switchNodeId: string | null;
	/** Template ID for the automation */
	templateId?: string;
	/** Callback when a segment should be added */
	onAddSegment?: (switchNodeId: string) => Promise<Api.ISwitchAutomationStep | void | null>;
	/** Whether the button is disabled */
	disabled?: boolean;
}

export function FloatingAddSegmentButton({
	switchNodeId,
	templateId: _templateId,
	onAddSegment,
	disabled = false,
}: FloatingAddSegmentButtonProps) {
	const [isAdding, setIsAdding] = useState(false);

	const handleClick = useCallback(async () => {
		if (!switchNodeId || !onAddSegment || isAdding || disabled) {
			return;
		}

		setIsAdding(true);
		try {
			await onAddSegment(switchNodeId);
		} finally {
			setIsAdding(false);
		}
	}, [switchNodeId, onAddSegment, isAdding, disabled]);

	// Don't render if no switch node is selected
	if (!switchNodeId) {
		return null;
	}

	return (
		<Panel position='bottom-right' className={css(styleSheet.panel)}>
			<button
				type='button'
				className={css(styleSheet.fab, isAdding && styleSheet.fabLoading)}
				onClick={handleClick}
				disabled={disabled || isAdding}
				aria-label='Add segment to conditional branch'
				title='Add a new path to this conditional branch'
			>
				{isAdding ? (
					<svg className={css(styleSheet.spinner)} viewBox='0 0 24 24' fill='none' stroke='currentColor'>
						<circle cx='12' cy='12' r='10' strokeWidth='2' strokeLinecap='round' strokeDasharray='60' />
					</svg>
				) : (
					<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
						<line x1='12' y1='5' x2='12' y2='19' strokeLinecap='round' />
						<line x1='5' y1='12' x2='19' y2='12' strokeLinecap='round' />
					</svg>
				)}
			</button>
			<div className={css(styleSheet.tooltip)}>Add Path</div>
		</Panel>
	);
}
